// RU-STRATDRILL SD-6: a strategy's DSL in plain words (pure). Returns [{label, text}] lines.

export const SESSION_NAMES = {
  1: "MCX open", 2: "NSE open", 3: "London open", 4: "US data", 5: "US open",
  6: "NSE close", 7: "London close", 8: "EIA crude", 9: "EIA natgas", 10: "MCX close",
};
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const FEATURES = {
  close: "price", open: "the bar's open", high: "the bar's high", low: "the bar's low", volume: "volume",
  vwap: "VWAP", or_high: "the opening-range high", or_low: "the opening-range low",
  session_or_high: "the session's opening-range high", session_or_low: "the session's opening-range low",
  day_range_pos: "the position in the day's range (0 = low, 1 = high)", rel_volume: "relative volume",
  vol_regime: "the volatility regime (ATR14 / ATR100)", gap_pct: "the opening gap %",
  prev_day_close: "the previous day's close", day_high: "the day's high", day_low: "the day's low",
};
const CMP = { ">": "is above", "<": "is below", ">=": "is at or above", "<=": "is at or below", "==": "equals", "=": "equals" };
const TF = { 1: "1-minute", 3: "3-minute", 5: "5-minute", 15: "15-minute", 30: "30-minute", 60: "1-hour", "1d": "daily", "1D": "daily", D: "daily" };

export function clock(min) {
  const m = Math.round(Number(min));
  return `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

function operand(x, inds) {
  if (typeof x === "number") return String(x);
  if (x == null) return "?";
  const key = String(x);
  const ind = inds[key];
  if (ind) {
    const t = String(ind.type || key).toUpperCase();
    return ind.period ? `${t}(${ind.period})` : t;
  }
  if (FEATURES[key]) return FEATURES[key];
  const m = key.match(/^([a-z]+)(\d+)$/i);                 // e.g. rsi14 without a declared indicator
  if (m) return `${m[1].toUpperCase()}(${m[2]})`;
  return key.replace(/_/g, " ");
}

function leaf(r, inds) {
  const { left, right } = r;
  const cmp = r.cmp || ">";
  if (left === "session" && (cmp === "==" || cmp === "=")) {
    if (Number(right) === 0) return "outside any session window";          // SD-13
    return `during the ${SESSION_NAMES[right] || `session ${right}`} window`;
  }
  if (left === "session_elapsed") {
    if (cmp === ">=" || cmp === ">") return `at least ${right} min into the session window`;
    if (cmp === "<=" || cmp === "<") return `no more than ${right} min into the session window`;
  }
  if (left === "minute_of_day" && typeof right === "number") {
    const word = { ">=": "at or after", ">": "after", "<=": "at or before", "<": "before", "==": "at", "=": "at" }[cmp] || cmp;
    return `${word} ${clock(right)} IST`;
  }
  if (left === "day_of_week" && typeof right === "number" && (cmp === "==" || cmp === "=")) return `on ${DAYS[right] || `day ${right}`}`;
  return `${operand(left, inds)} ${CMP[cmp] || cmp} ${operand(right, inds)}`;
}

export function ruleText(rule, inds = {}, depth = 0) {
  if (!rule || typeof rule !== "object") return "";
  if (Array.isArray(rule.rules)) {
    if (rule.rules.length === 1) return ruleText(rule.rules[0], inds, depth);   // a one-rule group needs no brackets
    const parts = rule.rules.map((r) => ruleText(r, inds, depth + 1)).filter(Boolean);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0];
    const joined = parts.join(String(rule.op || "and").toLowerCase() === "or" ? " OR " : " AND ");
    return depth > 0 ? `(${joined})` : joined;
  }
  return leaf(rule, inds);
}

const pct = (v) => `${Number(v)}%`;

function tfText(tf) {
  if (tf == null || tf === "") return null;
  return TF[tf] || (/^\d+$/.test(String(tf)) ? `${tf}-minute` : String(tf));
}

function legText(l) {
  const act = String(l.side || "BUY").toUpperCase() === "SELL" ? "sell" : "buy";
  let strike;
  if (l.option_strike != null && l.option_strike !== "") strike = String(l.option_strike);
  else {
    const off = Number(l.strike_offset || 0);
    strike = off === 0 ? "ATM" : `ATM${off > 0 ? "+" : ""}${off}`;
  }
  return `${act} ${l.qty || 1} ${strike} ${l.option_kind || l.instrument || ""}`.trim();
}

function premiumSide(legs) {
  const sells = legs.filter((l) => String(l.side || "BUY").toUpperCase() === "SELL").length;
  if (sells === 0) return "net long premium (debit)";
  if (sells === legs.length) return "net short premium (credit)";
  return "spread";
}

function indList(inds) {
  return (inds || []).filter(Boolean).map((i) => (i.period ? `${String(i.type || i.id).toUpperCase()}(${i.period})` : String(i.type || i.id).toUpperCase())).join(", ");
}

// SD-19: Python truthiness, as exit_plan's bool(dsl.get("breakeven_after_t1", True))
export function pyTruthy(v) {
  if (v === null || v === undefined || v === false || v === 0 || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v).length > 0;
  return !Number.isNaN(v);
}

// SD-21
export function holdText(min) {
  const m = Math.round(Number(min));
  if (!(m > 0)) return null;
  if (m < 60) return `${m} min`;
  const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
  if (m < 1440) return m % 60 === 0 ? plural(m / 60, "hour") : `${Math.floor(m / 60)} h ${m % 60} min`;
  const d = Math.floor(m / 1440);
  const h = Math.round((m % 1440) / 60);
  return h ? `${plural(d, "day")} ${plural(h, "hour")}` : plural(d, "day");
}

// `defaults` = the insights response's exit_defaults (the runner's own default stop), SD-18.
export default function dslToPlainEnglish(dsl, defaults = null) {
  if (!dsl || typeof dsl !== "object") return [];
  const inds = Object.fromEntries([...(dsl.indicators || []), ...(dsl.exit_indicators || [])]
    .filter((i) => i && i.id).map((i) => [i.id, i]));
  const out = [];
  const legs = Array.isArray(dsl.legs) ? dsl.legs.filter(Boolean) : [];
  const combo = legs.length > 0;                                               // SD-12: never long / Buy from `side`
  const side = String(dsl.side || "BUY").toUpperCase() === "SELL" ? "short" : "long";
  const instr = { OPT: "options", FUT: "futures", EQ: "equity" }[String(dsl.instrument_type || "").toUpperCase()];
  let inst;
  if (combo) {
    const name = dsl.combo_type ? String(dsl.combo_type).replace(/_/g, " ") : "option combo";   // short_straddle -> short straddle
    inst = `${dsl.symbol || "—"}, ${name}, ${premiumSide(legs)} — legs: ${legs.map(legText).join(", ")}`;
  } else {
    const contract = dsl.option_strike != null && dsl.option_strike !== ""
      ? ` ${dsl.option_strike}${dsl.option_kind ? ` ${dsl.option_kind}` : ""}` : dsl.option_kind ? ` ${dsl.option_kind}` : "";
    inst = `${dsl.symbol || "—"}${contract}, ${side}${instr ? `, ${instr}` : ""}`;
  }
  out.push({ label: "Instrument", text: inst });
  const tf = tfText(dsl.timeframe);
  const etf = tfText(dsl.entry_timeframe);
  const xtf = tfText(dsl.exit_timeframe);
  if (tf || etf || xtf) {
    out.push({ label: "Timeframe", text: [tf && `${tf} bars`, etf && etf !== tf && `entries on ${etf} bars`,
      xtf && xtf !== tf && `exit rules on ${xtf} bars`].filter(Boolean).join("; ") });
  }
  const entry = ruleText(dsl.entry, inds);
  const verb = combo ? "Enter the combo" : side === "long" ? "Buy" : "Sell";
  out.push({ label: "Entry", text: entry ? `${verb} when ${entry}` : "No entry rule" });
  const exit = ruleText(dsl.exit, inds);
  if (exit) out.push({ label: "Exit", text: `Exit when ${exit}` });
  if (Array.isArray(dsl.exit_indicators) && dsl.exit_indicators.length) {
    out.push({ label: "Exit indicators", text: indList(dsl.exit_indicators) });
  }
  // SD-14: the exit plan as services/exit_plan.py runs it
  const of = combo ? "of the net premium" : "from entry";
  const tp = Number(dsl.take_profit_pct || 0);
  const tp2 = Number(dsl.take_profit_2_pct || 0);
  if (Number(dsl.stop_loss_pct)) out.push({ label: "Stop", text: `${pct(dsl.stop_loss_pct)} ${of}` });
  else if (defaults && Number(defaults.stop_loss_pct)) {
    out.push({ label: "Stop", text: `default ${pct(defaults.stop_loss_pct)} ${of} (runner default)` });
  }
  if (tp && tp2) {
    const frac = Math.round(Number(dsl.t1_exit_fraction || 0.5) * 100);
    const be = !("breakeven_after_t1" in dsl) || pyTruthy(dsl.breakeven_after_t1);
    const unit = combo ? "whole baskets" : "the held quantity (paper: lots for futures / options, shares for equity; live: the broker quantity)";
    const t1 = `T1 ${pct(tp)} ${of} closes ${frac}% of ${unit} (rounded, at least one kept; with one held nothing closes at T1)`;
    out.push({ label: "Target", text: `${t1}; ${be ? "the stop then moves to break-even" : "no break-even: the stop is unchanged"}` });
    out.push({ label: "Second target", text: `T2 ${pct(tp2)} ${of} closes the rest` });
  } else if (tp) {
    out.push({ label: "Target", text: `${pct(tp)} ${of}, full exit` });
  }
  if (dsl.trailing_stop_pct) {
    const raw = dsl.trail_activate_pct;
    const given = raw !== null && raw !== undefined && raw !== "";
    let act;
    if (tp && tp2) {                       // exit_plan: active once T1 is done OR the move reaches the activation
      if (!given) act = "after T1";
      else if (Number(raw) < tp) act = Number(raw) === 0 ? "from entry" : `once the move reaches +${pct(raw)}`;
      else act = `at the earlier of +${pct(raw)} or after T1`;
    } else {
      act = given && Number(raw) !== 0 ? `once the move reaches +${pct(raw)}` : "from entry";
    }
    out.push({ label: "Trailing stop", text: `${pct(dsl.trailing_stop_pct)} behind the best price, ${act}` });
  }
  if (holdText(dsl.max_hold_minutes)) out.push({ label: "Max hold", text: holdText(dsl.max_hold_minutes) });
  if (dsl.exit_time) out.push({ label: "Exit time", text: `flat by ${dsl.exit_time} IST` });
  if (dsl.protection_pct) {
    out.push({ label: "Order protection", text: `live orders are protected limits within ${pct(dsl.protection_pct)} of the reference price` });
  }
  const sch = dsl.schedule;
  if (sch && typeof sch === "object") {
    const days = Array.isArray(sch.days) && sch.days.length ? sch.days.join(", ") : "MON–FRI";
    const win = sch.start_time || sch.end_time ? ` ${sch.start_time || "open"}–${sch.end_time || "close"} IST` : "";
    out.push({ label: "Trading window", text: `${days}${win}` });
  }
  return out;
}
